import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Magnet, Lock, Download, FileText, LogOut, Users, ShoppingCart, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api, API, formatApiErrorDetail } from "@/lib/api";

const EMPTY_FORM = { business_name: "", website_url: "", industry: "", target_audience: "", geographic_focus: "", extra_notes: "" };

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("mt33_admin_token"));
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [leads, setLeads] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, l, o] = await Promise.all([api.get("/admin/reports"), api.get("/admin/leads"), api.get("/admin/orders")]);
      setReports(r.data); setLeads(l.data); setOrders(o.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("mt33_admin_token"); setAuthed(false); }
    }
  }, []);
  useEffect(() => { if (authed) load(); }, [authed, load]);

  const login = async () => {
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { password });
      localStorage.setItem("mt33_admin_token", res.data.token);
      setAuthed(true);
      toast.success("Welcome, admin");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  const logout = () => { localStorage.removeItem("mt33_admin_token"); setAuthed(false); };

  const generate = async () => {
    if (!form.business_name.trim()) { toast.error("Business name is required"); return; }
    setGenerating(true);
    try {
      const res = await api.post("/admin/reports/generate", form);
      const id = res.data.id;
      toast.success("Generation started — this takes several minutes.");
      setForm(EMPTY_FORM);
      load();
      const poll = async () => {
        try {
          const r = await api.get(`/reports/${id}`);
          if (r.data.status === "complete") { setGenerating(false); toast.success("Report ready — download below."); load(); }
          else if (r.data.status === "failed") { setGenerating(false); toast.error("Generation failed. Try again."); load(); }
          else setTimeout(poll, 5000);
        } catch { setTimeout(poll, 5000); }
      };
      setTimeout(poll, 6000);
    } catch (e) {
      setGenerating(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Generation failed");
    }
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-5">
        <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8">
          <div className="mb-6 flex flex-col items-center gap-2">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald9 border border-gold/40"><Lock className="h-5 w-5 text-gold" /></div>
            <h1 className="font-display text-xl font-bold">MT33 Admin</h1>
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Password</Label>
              <Input type="password" data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} placeholder="••••••••" className="bg-muted/40 border-border" /></div>
            <Button onClick={login} disabled={loading} data-testid="admin-login-btn" className="w-full rounded-full bg-gradient-to-r from-emerald9 via-royal to-gold py-6 font-bold text-white">{loading ? "..." : "Sign In"}</Button>
          </div>
          <Link to="/" className="mt-4 block text-center text-xs text-muted-foreground hover:text-gold">← Back to site</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald9 border border-gold/40"><Magnet className="h-5 w-5 text-gold" /></div>
            <span className="font-display text-lg font-bold">MT33 <span className="text-gold">Admin</span></span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/"><Button variant="ghost" size="sm" className="text-muted-foreground hover:text-gold">View Site</Button></Link>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-btn" className="text-muted-foreground hover:text-gold"><LogOut className="mr-1.5 h-4 w-4" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-8 px-5 py-8">
        {/* Report generator */}
        <section>
          <h2 className="mb-4 flex items-center gap-2 font-display text-2xl font-bold"><Sparkles className="h-5 w-5 text-gold" /> Generate Customer Report</h2>
          <div className="rounded-2xl border border-border bg-card p-6">
            <p className="mb-4 text-sm text-muted-foreground">Enter the customer's business data. The autonomous AI generates a detailed 50–75 page 7-step campaign report as a downloadable PDF (takes a few minutes).</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Business Name *</Label><Input data-testid="gen-business-name" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Acme Co." className="bg-muted/40 border-border" /></div>
              <div className="space-y-1.5"><Label>Website URL</Label><Input data-testid="gen-website-url" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="https://acme.com" className="bg-muted/40 border-border" /></div>
              <div className="space-y-1.5"><Label>Industry</Label><Input data-testid="gen-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="SaaS, Fitness..." className="bg-muted/40 border-border" /></div>
              <div className="space-y-1.5"><Label>Geographic Focus</Label><Input data-testid="gen-geo" value={form.geographic_focus} onChange={(e) => setForm({ ...form, geographic_focus: e.target.value })} placeholder="US, London..." className="bg-muted/40 border-border" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Target Audience</Label><Input data-testid="gen-audience" value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} placeholder="Who buys from them?" className="bg-muted/40 border-border" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>All Other Business Data / Notes</Label><Textarea data-testid="gen-notes" value={form.extra_notes} onChange={(e) => setForm({ ...form, extra_notes: e.target.value })} placeholder="Products, offers, price points, goals, current challenges, anything else the AI should know…" className="min-h-[90px] bg-muted/40 border-border" /></div>
            </div>
            <Button onClick={generate} disabled={generating} data-testid="gen-submit" className="mt-5 w-full rounded-full bg-gradient-to-r from-emerald9 via-royal to-gold py-6 text-base font-bold text-white">
              {generating ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Generating… (several minutes)</> : <><Sparkles className="mr-2 h-5 w-5" /> Generate 50–75 Page Report</>}
            </Button>
          </div>
        </section>

        {/* Generated reports */}
        <section>
          <h2 className="mb-4 flex items-center gap-2 font-display text-2xl font-bold"><FileText className="h-5 w-5 text-gold" /> Generated Reports</h2>
          <div className="space-y-2">
            {reports.length === 0 && <p className="text-sm text-muted-foreground">No reports yet.</p>}
            {reports.map((r) => (
              <div key={r.id} data-testid={`report-row-${r.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4">
                <div>
                  <p className="font-medium text-foreground">{r.business_name}</p>
                  <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()} · {r.industry || "—"} · {r.geographic_focus || "—"}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={r.status === "complete" ? "bg-emerald9 text-gold" : r.status === "failed" ? "bg-destructive text-destructive-foreground" : "bg-muted text-muted-foreground"}>{r.status === "processing" ? "generating…" : r.status}</Badge>
                  {r.status === "complete" && (
                    <a href={`${API}/reports/${r.id}/pdf`} target="_blank" rel="noopener noreferrer" data-testid={`download-${r.id}`}>
                      <Button size="sm" className="rounded-full bg-gradient-to-r from-emerald9 via-royal to-gold font-semibold text-white"><Download className="mr-1.5 h-3.5 w-3.5" /> PDF</Button>
                    </a>)}
                </div>
              </div>))}
          </div>
        </section>

        {/* Orders */}
        <section>
          <h2 className="mb-4 flex items-center gap-2 font-display text-2xl font-bold"><ShoppingCart className="h-5 w-5 text-gold" /> Orders</h2>
          <div className="space-y-2">
            {orders.length === 0 && <p className="text-sm text-muted-foreground">No orders yet. Orders are captured when a visitor enters their details on a payment button.</p>}
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-row-${o.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{o.name} · {o.email}</p>
                  <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()}</p>
                </div>
                <Badge className="bg-emerald9 text-gold">{o.tier_label}</Badge>
              </div>))}
          </div>
        </section>

        {/* Ebook leads */}
        <section>
          <h2 className="mb-4 flex items-center gap-2 font-display text-2xl font-bold"><Users className="h-5 w-5 text-gold" /> Free Ebook Leads</h2>
          <div className="space-y-2">
            {leads.length === 0 && <p className="text-sm text-muted-foreground">No leads captured yet.</p>}
            {leads.map((l) => (
              <div key={l.id} data-testid={`lead-row-${l.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-3">
                <div className="min-w-0"><p className="truncate text-sm font-medium text-foreground">{l.name}</p><p className="text-xs text-muted-foreground">{l.email}</p></div>
                <span className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</span>
              </div>))}
          </div>
        </section>
      </main>
    </div>
  );
}
